import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { Document } from "react-pdf"
import { PDF_OPTIONS } from "../lib/pdfWorker"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

import { AppShell } from "../components/AppShell"
import { BookPageOverlay } from "../components/BookPageOverlay"
import { SectionSummary } from "../components/SectionSummary"
import { DiaryValuesPanel } from "../components/DiaryValuesPanel"
import { UploadFab } from "../components/UploadFab"
import { ReceiptSortModal } from "../components/ReceiptSortModal"
import type { ProcessResponse } from "../api/receipts"
import { useApi } from "../api/hooks"
import { openForm, saveFormValues, type FormInstance } from "../api/templates"
import { useT } from "../lib/i18n"

const SAVE_DEBOUNCE_MS = 800

export function FormViewerPage() {
  const { templateId = "", instanceKey = "main" } = useParams<{ templateId: string; instanceKey: string }>()
  const api = useApi()
  const { t } = useT()
  const [form, setForm] = useState<FormInstance | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [current, setCurrent] = useState(0)
  const [values, setValues] = useState<Record<string, string>>({})
  const [sortData, setSortData] = useState<ProcessResponse | null>(null)
  const [showSummary, setShowSummary] = useState(true)
  const [showEntries, setShowEntries] = useState(true)
  const [pageWidth, setPageWidth] = useState(800)
  const [scale, setScale] = useState(1)
  const [visible, setVisible] = useState<Set<number>>(() => new Set())
  const [docReady, setDocReady] = useState(false)
  const [pdfErr, setPdfErr] = useState<string | null>(null)
  const [slow, setSlow] = useState(false)
  const [probe, setProbe] = useState<string | null>(null)
  const [pdfData, setPdfData] = useState<{ data: Uint8Array } | null>(null)
  const [savingState, setSavingState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const centerRef = useRef<HTMLDivElement | null>(null)
  const pan = useRef<{ x: number; y: number; sl: number; st: number } | null>(null)
  const visibleRef = useRef<Set<number>>(new Set())
  const saveTimerRef = useRef<number | null>(null)
  const dirtyRef = useRef<Record<string, string>>({})
  const valuesRef = useRef<Record<string, string>>({})
  const formRef = useRef<FormInstance | null>(null)

  // Keep refs in sync
  useEffect(() => { valuesRef.current = values }, [values])
  useEffect(() => { formRef.current = form }, [form])

  // Download the PDF ourselves on the main thread and feed it to react-pdf as a Uint8Array.
  // This avoids (a) passing a blob: URL which violates connect-src CSP in prod, and
  // (b) re-fetching the 84-page PDF every time the presigned S3 URL changes (on token refresh).
  // Because we disabled the Web Worker globally (in favor of the fake main-thread worker),
  // pdf.js will NOT detach the ArrayBuffer, making this approach 100% safe.
  const pdfLoadedFor = useRef<string | null>(null)
  useEffect(() => {
    const tid = form?.template?.template_id
    const url = form?.template_url
    if (!tid || !url || pdfLoadedFor.current === tid) return
    pdfLoadedFor.current = tid
    setPdfErr(null)
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer() })
      .then((buffer) => setPdfData({ data: new Uint8Array(buffer) }))
      .catch((e) => { pdfLoadedFor.current = null; setPdfErr(e instanceof Error ? e.message : String(e)) })
  }, [form?.template?.template_id, form?.template_url])

  // If the PDF doesn't finish loading within a few seconds (and hasn't errored),
  // surface a diagnostic. Also run a probe fetch from the main thread: if that
  // succeeds while pdf.js stays stuck, the worker is the culprit; if the probe
  // fails, a network/extension block is swallowing the request.
  useEffect(() => {
    if (!form || docReady || pdfErr) return
    const url = form.template_url
    const id = window.setTimeout(async () => {
      setSlow(true)
      try {
        const r = await fetch(url, { headers: { Range: "bytes=0-1023" } })
        setProbe(`fetch ok: HTTP ${r.status}`)
      } catch (e) {
        setProbe(`fetch BLOCCATA: ${e instanceof Error ? e.message : String(e)}`)
      }
    }, 10000)
    return () => window.clearTimeout(id)
  }, [form, docReady, pdfErr])

  useEffect(() => {
    if (!templateId) return
    openForm(api, templateId, instanceKey)
      .then((r) => {
        setForm(r)
        setValues(r.values || {})
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
  }, [api, templateId, instanceKey])

  useEffect(() => {
    if (!centerRef.current) return
    const el = centerRef.current
    const update = () => setPageWidth(Math.max(320, el.clientWidth - 32))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener("resize", update)
    return () => { ro.disconnect(); window.removeEventListener("resize", update) }
  }, [form])

  useEffect(() => {
    if (!form) return
    const obs = new IntersectionObserver((entries) => {
      const v = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
      if (v) {
        const idx = Number((v.target as HTMLElement).dataset.pageIndex)
        if (Number.isFinite(idx)) setCurrent(idx)
      }
    }, { threshold: [0.2, 0.5, 0.8] })
    for (const k of Object.keys(pageRefs.current)) {
      const el = pageRefs.current[Number(k)]
      if (el) obs.observe(el)
    }
    return () => obs.disconnect()
  }, [form, docReady])

  // Virtualisation: only mount react-pdf <Page> for pages near the viewport, so
  // the pdf.js worker isn't choked by rendering all pages at once.
  useEffect(() => {
    if (!form) return
    const obs = new IntersectionObserver((entries) => {
      let changed = false
      for (const e of entries) {
        const idx = Number((e.target as HTMLElement).dataset.pageIndex)
        if (!Number.isFinite(idx)) continue
        if (e.isIntersecting) {
          if (!visibleRef.current.has(idx)) { visibleRef.current.add(idx); changed = true }
        } else if (visibleRef.current.has(idx)) {
          visibleRef.current.delete(idx); changed = true
        }
      }
      if (changed) setVisible(new Set(visibleRef.current))
    }, { root: centerRef.current, rootMargin: "1500px 0px", threshold: 0 })
    for (const k of Object.keys(pageRefs.current)) {
      const el = pageRefs.current[Number(k)]
      if (el) obs.observe(el)
    }
    return () => obs.disconnect()
  }, [form, docReady])

  const clampScale = (s: number) => Math.min(3, Math.max(0.5, s))
  const zoom = useCallback((delta: number) => setScale((s) => clampScale(s + delta)), [])

  // Drag-to-pan: start a pan only when the pointer lands on empty PDF area, so
  // input cells stay clickable/fillable.
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    if (target.closest("input, textarea, select, button, a")) return
    const el = centerRef.current
    if (!el) return
    pan.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop }
    el.setPointerCapture(e.pointerId)
    el.style.cursor = "grabbing"
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const el = centerRef.current
    if (!el || !pan.current) return
    el.scrollLeft = pan.current.sl - (e.clientX - pan.current.x)
    el.scrollTop = pan.current.st - (e.clientY - pan.current.y)
  }, [])

  const endPan = useCallback((e: React.PointerEvent) => {
    const el = centerRef.current
    if (!el || !pan.current) return
    pan.current = null
    try { el.releasePointerCapture(e.pointerId) } catch { /* already released */ }
    el.style.cursor = "grab"
  }, [])

  // Ctrl/⌘ + wheel (and trackpad pinch) zooms. React's onWheel is passive, so
  // attach a non-passive native listener to be able to preventDefault.
  useEffect(() => {
    const el = centerRef.current
    if (!el) return
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      zoom(e.deltaY < 0 ? 0.1 : -0.1)
    }
    el.addEventListener("wheel", handler, { passive: false })
    return () => el.removeEventListener("wheel", handler)
  }, [zoom, form])

  const flushSave = useCallback(async () => {
    const currentForm = formRef.current
    if (!currentForm) return
    const dirty = dirtyRef.current
    if (Object.keys(dirty).length === 0) return
    dirtyRef.current = {}
    setSavingState("saving")
    try {
      await saveFormValues(api, currentForm.form_id, valuesRef.current)
      setSavingState("saved")
      window.setTimeout(() => setSavingState("idle"), 1500)
    } catch {
      setSavingState("error")
    }
  }, [api])

  const onCellChange = useCallback((cell: { code: string }, value: string) => {
    setValues((prev) => ({ ...prev, [cell.code]: value }))
    dirtyRef.current[cell.code] = value
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    saveTimerRef.current = window.setTimeout(() => { void flushSave() }, SAVE_DEBOUNCE_MS)
  }, [flushSave])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current)
        const currentForm = formRef.current
        if (currentForm) void saveFormValues(api, currentForm.form_id, valuesRef.current).catch(() => undefined)
      }
    }
  }, [api])

  const onJump = useCallback((index: number) => {
    const el = pageRefs.current[index]
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setCurrent(index) }
  }, [])

  // Map each cell code to its 0-based page index, so clicking a diary value can
  // jump to the page that holds that cell.
  const codeToPage = useMemo(() => {
    const m: Record<string, number> = {}
    if (form) {
      for (const [pageNumStr, cs] of Object.entries(form.cells.pages)) {
        const idx = Number(pageNumStr) - 1
        for (const c of cs) m[c.code] = idx
      }
    }
    return m
  }, [form])

  // Jump to the page holding `code`, then (once the virtualised page mounts and
  // pdf.js finishes rendering) scroll the matching input into view, focus it and
  // flash a highlight. The page render is async, so poll for the input.
  const onSelectCell = useCallback((code: string) => {
    const idx = codeToPage[code]
    if (idx == null) return
    onJump(idx)
    let tries = 0
    const tryFocus = () => {
      const root = centerRef.current
      const el = root?.querySelector<HTMLInputElement>(`input[data-cell-code="${CSS.escape(code)}"]`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
        el.focus({ preventScroll: true })
        el.select()
        const prev = el.style.boxShadow
        el.style.boxShadow = "0 0 0 3px rgba(245,197,66,0.9)"
        window.setTimeout(() => { el.style.boxShadow = prev }, 1600)
      } else if (tries++ < 40) {
        window.setTimeout(tryFocus, 100)
      }
    }
    window.setTimeout(tryFocus, 150)
  }, [codeToPage, onJump])


  const { lang } = useT()
  const pages = useMemo(() => {
    if (!form) return []
    const total = form.template.total_pages
    const sections = form.template.sections
    const labelFor = (s: { label: string; labels?: Record<string, string> }) =>
      (s.labels?.[lang]) ?? s.label
    return Array.from({ length: total }, (_, i) => {
      const pageNum = i + 1
      const s = sections.find((x) => x.first_page <= pageNum && pageNum <= x.last_page)
      const baseLabel = s ? labelFor(s) : `Page ${pageNum}`
      return {
        index: i,
        page_num: pageNum,
        title: s && s.last_page > s.first_page
          ? `${baseLabel} (${pageNum - s.first_page + 1}/${s.last_page - s.first_page + 1})`
          : baseLabel,
        kind: s?.kind ?? "other",
        week_idx: s?.week_idx,
        fields: [],
      }
    })
  }, [form, lang])

  const summaryGroups = useMemo(() => {
    if (!form) return []
    return [{ label: form.template.name, kinds: form.template.sections.map((s) => s.kind) }]
  }, [form])

  const renderedWidth = Math.round(pageWidth * scale)
  const pageRatio = form?.cells.page_size_pt
    ? form.cells.page_size_pt[1] / form.cells.page_size_pt[0]
    : 1.414

  return (
    <AppShell fluid>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>{form ? form.template.name : t("loading")}</h1>
        <span style={{ color: "var(--muted)", fontSize: 13 }}>
          {form && `${form.template.lang.toUpperCase()} · v${form.template.version} · ${form.template.total_pages} pages · ${instanceKey}`}
        </span>
        <span style={{ fontSize: 12, color: savingState === "error" ? "#ff7a85" : "var(--muted)" }}>
          {savingState === "saving" && t("saving")}
          {savingState === "saved" && `✓ ${t("saved_ago")}`}
          {savingState === "error" && t("save_error")}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <button type="button" onClick={() => setShowSummary((v) => !v)} style={{ ...zoomBtn, width: "auto", padding: "0 8px", fontSize: 12, background: showSummary ? "var(--surface)" : "var(--border)" }} title="Mostra/nascondi sommario">☰ Sommario</button>
          <button type="button" onClick={() => setShowEntries((v) => !v)} style={{ ...zoomBtn, width: "auto", padding: "0 8px", fontSize: 12, background: showEntries ? "var(--surface)" : "var(--border)" }} title="Mostra/nascondi voci della settimana">€ Voci</button>
          <span style={{ width: 1, height: 20, background: "var(--border)", margin: "0 2px" }} />
          <button type="button" onClick={() => zoom(-0.1)} style={zoomBtn} title="Zoom out">−</button>
          <button type="button" onClick={() => setScale(1)} style={{ ...zoomBtn, width: "auto", padding: "0 8px", fontSize: 12 }} title="Reset zoom">
            {Math.round(scale * 100)}%
          </button>
          <button type="button" onClick={() => zoom(0.1)} style={zoomBtn} title="Zoom in">+</button>
        </div>
      </div>

      {err && <p style={{ color: "#ff7a85" }}>{err}</p>}
      {!err && !form && <p style={{ color: "var(--muted)" }}>{t("loading_diary")}</p>}

      {form && (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", height: "calc(100dvh - 180px)" }}>
          {showSummary && (
            <SectionSummary
              pages={pages}
              groups={summaryGroups}
              current={current}
              onJump={onJump}
            />
          )}
          <div
            ref={centerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            style={{
              flex: 1, height: "100%", overflow: "auto",
              background: "var(--surface)", borderRadius: "var(--radius)",
              border: "1px solid var(--border)", padding: 16,
              cursor: "grab", touchAction: "none",
            }}
          >
            {(pdfErr || slow) && (
              <div style={{
                margin: "0 0 16px", padding: 12, borderRadius: 8,
                border: "1px solid #e0b400", background: "rgba(245,197,66,0.12)",
                color: "var(--text)", fontSize: 13, lineHeight: 1.5,
              }}>
                <strong>Il PDF non si carica nel browser.</strong>{pdfErr ? ` (${pdfErr})` : " (timeout)"}<br />
                La richiesta al file PDF sembra bloccata lato browser (es. Adblock/estensione).
                Prova ad <a href={form.template_url} target="_blank" rel="noreferrer" style={{ color: "#0a7", fontWeight: 600 }}>aprire il PDF in una nuova scheda</a>:
                se non si apre nemmeno lì, è il browser/estensione a bloccarlo.
                {probe && <><br /><strong>Probe:</strong> {probe}</>}
              </div>
            )}
            <Document
              file={pdfData}
              options={PDF_OPTIONS}
              onLoadSuccess={() => setDocReady(true)}
              onLoadError={(e) => { console.error("[pdf] load error", e); setPdfErr(e?.message || String(e)) }}
              onSourceError={(e) => { console.error("[pdf] source error", e); setPdfErr(e?.message || String(e)) }}
              loading={<p style={{ color: "var(--muted)" }}>{t("loading_template")}</p>}
              error={<p style={{ color: "#ff7a85" }}>{t("unable_pdf")}</p>}
            >
              {pages.map((p) => (
                <div
                  key={p.index}
                  ref={(el) => { pageRefs.current[p.index] = el }}
                  data-page-index={p.index}
                  style={{ marginBottom: 16, display: "flex", justifyContent: "safe center" }}
                >
                  {visible.has(p.index) ? (
                    <BookPageOverlay
                      pageIndex={p.index}
                      pageNum={p.page_num}
                      width={renderedWidth}
                      cells={form.cells.pages[String(p.page_num)] ?? []}
                      values={values}
                      onChange={onCellChange}
                    />
                  ) : (
                    <div style={{
                      width: renderedWidth,
                      height: Math.round(renderedWidth * pageRatio),
                      background: "white",
                      boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
                    }} />
                  )}
                </div>
              ))}
            </Document>
          </div>
          {showEntries && <DiaryValuesPanel cells={Object.values(form.cells.pages).flat()} values={values} onSelect={onSelectCell} />}
        </div>
      )}

      <UploadFab
        onUploaded={() => {
          if (templateId && instanceKey) {
            openForm(api, templateId, instanceKey).then((r) => {
              setForm(r)
              setValues((prev) => ({ ...r.values, ...prev, ...dirtyRef.current }))
            }).catch(() => undefined)
          }
        }}
        onProcessed={(res) => { if (res.entries?.length) setSortData(res) }}
      />

      {sortData && form && (
        <ReceiptSortModal
          entries={sortData.entries}
          merchant={sortData.receipt.merchant}
          total={sortData.receipt.total_eur}
          cells={Object.values(form.cells.pages).flat()}
          weekKey={instanceKey}
          onClose={() => setSortData(null)}
          onApply={async (assignments) => {
            const next = { ...valuesRef.current }
            for (const { code, amount } of assignments) {
              const existing = parseFloat(next[code] || "0") || 0
              next[code] = (existing + amount).toFixed(2)
            }
            setValues(next)
            if (form.form_id) await saveFormValues(api, form.form_id, next)
          }}
        />
      )}
    </AppShell>
  )
}

const zoomBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "grid",
  placeItems: "center",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  cursor: "pointer",
  fontSize: 16,
  lineHeight: 1,
}
